import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const getOrCreateId = (key) => {
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const id = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  localStorage.setItem(key, id);
  return id;
};

const parseJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch (error) {
    return null;
  }
};

const getCurrentUser = () => {
  const candidates = [
    ['admin', parseJson('adminUser')],
    ['teacher', parseJson('teacherUser')],
    ['staff', parseJson('staffUser')],
    ['security', parseJson('securityUser')],
    ['parent', parseJson('parentUser')],
    ['student', parseJson('studentUser')]
  ];
  const [role, user] = candidates.find(([, value]) => value) || ['visitor', null];
  return {
    userRole: role,
    userId: user?.id || user?.username || user?.email || '',
    userName: user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || user?.username || ''
  };
};

const getCampaignData = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm: {
      source: params.get('utm_source') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
      term: params.get('utm_term') || '',
      content: params.get('utm_content') || ''
    },
    adClickIds: {
      gclid: params.get('gclid') || '',
      fbclid: params.get('fbclid') || '',
      msclkid: params.get('msclkid') || ''
    }
  };
};

const sendActivity = (event) => {
  const sessionId = getOrCreateId('ctSessionId');
  const visitorId = getOrCreateId('ctVisitorId');
  const user = getCurrentUser();
  const campaign = getCampaignData();
  const payload = {
    ...event,
    ...user,
    ...campaign,
    sessionId,
    visitorId,
    pageUrl: window.location.href,
    path: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || 'direct',
    source: 'client'
  };

  fetch(`${API_ENDPOINTS.BASE_URL}/api/tracking/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true
  }).catch(() => {});
};

const getElementLabel = (element) => {
  if (!element) return '';
  const label = element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    element.innerText ||
    element.textContent ||
    element.name ||
    element.id ||
    element.tagName;
  return String(label || '').replace(/\s+/g, ' ').trim().slice(0, 180);
};

const ActivityTracker = () => {
  const location = useLocation();

  useEffect(() => {
    sendActivity({
      type: 'page_view',
      action: 'view_page',
      label: document.title
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (event) => {
      const target = event.target.closest('button, a, [role="button"], input[type="submit"]');
      if (!target) return;
      sendActivity({
        type: 'click',
        action: target.tagName === 'A' ? 'click_link' : 'click_button',
        label: getElementLabel(target),
        metadata: {
          tag: target.tagName,
          href: target.href || '',
          id: target.id || '',
          className: String(target.className || '').slice(0, 240)
        }
      });
    };

    const handleSubmit = (event) => {
      sendActivity({
        type: 'form_submit',
        action: 'submit_form',
        label: getElementLabel(event.target),
        metadata: {
          id: event.target.id || '',
          name: event.target.getAttribute('name') || '',
          action: event.target.getAttribute('action') || window.location.pathname
        }
      });
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
    };
  }, []);

  return null;
};

export default ActivityTracker;
